// Heute: the running timer, the day's figures, the activity timeline, agent sessions and today's projects.
// The web version of the design system's Today reference (design/components/TodayScreen).
import { useMemo } from 'react';
import { dayRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { displayTitle, runningEntry } from '../model/data.ts';
import { agentCountsLabel, buildSnapshot, timelineRange, type DaySnapshot, type SessionView } from '../model/snapshot.ts';
import { Banner, EmptyState, Loading, ScreenHeader, SectionHeader } from '../components/common.tsx';
import { AssignProjectMenu, RowButton, SessionCard, TimerCard } from '../components/pickers.tsx';
import { useFmt, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button, Monogram, ProjectChip, ProjectRow, SplitBar, StatTile } from '../ui/ds.tsx';
import { ActivityTimeline, type TimelineLane } from '../ui/timeline.tsx';

export function Today() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(1000);
  const today = useMemo(() => dayRange(now, store.timeZone), [Math.floor(now / 60_000), store.timeZone]);
  const loaded = useRange(today, false);
  const data = loaded.data;
  const snap = data ? buildSnapshot(store.ws, data, today, now) : undefined;
  const running = data ? runningEntry(data, store.me?.workspace?.memberId) : undefined;

  return (
    <div className="screen">
      <ScreenHeader title={t('Heute')} subtitle={fmt.todayLabel(now)}>
        <Button icon="plus" onClick={() => store.openSheet({ kind: 'entry', entryId: null, day: null })}>{t('Eintrag')}</Button>
        <Button icon="share" onClick={() => store.openSheet({ kind: 'export', projectId: null })} disabled={store.ws.projects.length === 0}>{t('Exportieren')}</Button>
      </ScreenHeader>
      {snap && snap.unassigned.length > 0 ? (
        <Banner icon="folder" actions={data ? <AssignProjectMenu session={(snap.unassigned[0] as SessionView).record} loaded={data.sessions} /> : null}>
          {t('%@ ohne Projekt: %@ Agent-Zeit wird nicht abgerechnet.', t('%lld Sessions', snap.unassigned.length),
            fmt.duration(snap.unassigned.reduce((n, s) => n + s.countedMs, 0)))}
        </Banner>
      ) : null}
      <TimerCard running={running} now={now} agentsCounting={snap?.countingAgents ?? 0} />
      {!snap ? (loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />) : snap.isEmpty ? (
        <EmptyState icon="clock" title={t('Noch nichts erfasst')} message={t('Starte einen Timer oder verbinde einen Agent.')}>
          <Button icon="plug" onClick={() => navigate({ name: 'integrations' })}>{t('Agent verbinden')}</Button>
        </EmptyState>
      ) : (
        <>
          <Stats snap={snap} />
          <section className="tl-card panel">
            <SectionHeader title={t('Aktivität')} caption={t('Grün zählt · pro Projekt werden Überschneidungen nur einmal abgerechnet')} />
            <ActivityTimeline {...timelineRange(snap)} now={now} lanes={lanes(snap, data?.entries ?? [], t('Eigene Zeit'))} />
          </section>
          <div className="cols today-cols">
            <div className="stack">
              <SectionHeader title={t('Agent-Sessions')} caption={agentCountsLabel(t, snap.countingAgents, snap.waitingAgents)} />
              {snap.sessions.length === 0 ? (
                <EmptyState icon="terminal" title={t('Heute noch keine Agent-Sessions')} message={t('Sobald ein Agent arbeitet, erscheint seine Session hier.')} />
              ) : (
                <>
                  {snap.sessions.filter((s) => s.isLive).slice(0, 6).map((s) => <SessionCard key={s.record.id} view={s} now={now} loaded={data?.sessions ?? []} />)}
                  <EarlierSessions sessions={snap.sessions.filter((s) => !s.isLive)} loaded={data?.sessions ?? []} />
                </>
              )}
            </div>
            <div className="stack">
              <SectionHeader title={t('Projekte heute')} caption={t('%@ abrechenbar', fmt.money(snap.amountCents))} />
              {snap.projectDays.length === 0 ? (
                <EmptyState icon="folder" title={t('Noch keine Projektzeit')} message={t('Ordne deinen Timer oder eine Session einem Projekt zu.')} />
              ) : (
                <>
                  <div className="tl-card plist">
                    {snap.projectDays.map((d) => (
                      <RowButton key={d.project.id} onClick={() => navigate({ name: 'project', id: d.project.id })}>
                        <ProjectRow compact name={d.project.name} client={d.client?.name} color={d.project.colorSlot} humanMs={d.totals.humanMs}
                          agentMs={d.totals.agentMs} mergedMs={d.totals.mergedMs} running={d.running}
                          amount={d.project.rateCents !== null && d.project.billable ? fmt.money(d.totals.amountCents, d.project.currency) : undefined} />
                      </RowButton>
                    ))}
                  </div>
                  <div className="split-legend"><SplitBar human={snap.humanMs} agent={snap.agentMs} legend height={8} /></div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Stats({ snap }: { snap: DaySnapshot }) {
  const t = useStore().t;
  const fmt = useFmt();
  return (
    <div className="stats">
      <StatTile label={t('Abgerechnet')} value={fmt.hm(snap.mergedMs)} unit="h"
        hint={snap.overlapMs > 0 ? t('%@ h Summe · Überschneidung 1× gezählt', fmt.hm(snap.rawMs)) : t('Keine Überschneidung')} />
      <StatTile label={t('Eigene Zeit')} accent="human" value={fmt.hm(snap.humanMs)} unit="h" hint={t('%lld Einträge', snap.entryCount)} />
      <StatTile label={t('Agent-Zeit')} accent="agent" value={fmt.hm(snap.agentMs)} unit="h" hint={t('%@ h gewartet, nicht gezählt', fmt.hm(snap.waitingMs))} />
      <StatTile label={t('Abrechenbar')} value={fmt.number(snap.amountCents / 100)} unit="€"
        hint={snap.hasInternalTime ? t('ohne interne Projekte') : t('Satz × zusammengeführte Zeit')} />
    </div>
  );
}

function lanes(snap: DaySnapshot, entries: readonly { startAt: number; endAt: number | null; description: string; deletedAt: number | null }[], yours: string): TimelineLane[] {
  const out: TimelineLane[] = [];
  if (snap.humanLane.length > 0) {
    const inDay = entries.filter((e) => e.deletedAt === null && e.startAt < snap.range.end && (e.endAt ?? snap.now) > snap.range.start);
    out.push({
      id: 'me', label: yours, source: 'human',
      segments: inDay.map((e) => ({
        start: Math.max(e.startAt, snap.range.start), end: Math.min(e.endAt ?? snap.now, snap.range.end), kind: 'counted' as const,
        ...(e.description ? { label: e.description } : {}),
      })),
    });
  }
  for (const s of snap.sessions.slice(0, 8)) {
    if (s.segments.length === 0) continue;
    out.push({ id: s.record.id, label: s.provider.name + (s.record.repoName ? ` · ${s.record.repoName}` : ''), source: 'agent', segments: s.segments });
  }
  return out;
}

function EarlierSessions({ sessions, loaded }: { sessions: SessionView[]; loaded: Parameters<typeof AssignProjectMenu>[0]['loaded'] }) {
  const t = useStore().t;
  const fmt = useFmt();
  if (sessions.length === 0) return null;
  return (
    <div className="tl-card earlier">
      <div className="tl-label earlier__label">{t('Früher heute')}</div>
      {sessions.slice(0, 12).map((s) => (
        <div key={s.record.id} className="earlier__row">
          <Monogram text={s.provider.mono} provider={s.provider.id} size="sm" />
          <RowButton className="earlier__main" onClick={() => navigate({ name: 'session', id: s.record.id })} label={displayTitle(s.record)}>
            <span className="earlier__title" title={displayTitle(s.record)}>{displayTitle(s.record)}</span>
          </RowButton>
          <span className="earlier__num">{fmt.duration(s.countedMs)}<small>{t('gezählt')}</small></span>
          <div className="earlier__project">
            {s.project ? <ProjectChip name={s.project.name} color={s.project.colorSlot} plain /> : <AssignProjectMenu session={s.record} loaded={loaded} compact />}
          </div>
        </div>
      ))}
    </div>
  );
}
